/** Runtime platform helpers: the same build runs in a browser and inside the Tauri desktop shell. */
export const isTauri = typeof window !== 'undefined' && '__TAURI_INTERNALS__' in window;

let audio: HTMLAudioElement | undefined;
/** Short two-tone chime (generated, no asset needed). */
export function playChime(): void {
  try {
    const ctx = new AudioContext();
    const now = ctx.currentTime;
    [880, 1320].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, now + i * 0.16);
      gain.gain.exponentialRampToValueAtTime(0.25, now + i * 0.16 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + i * 0.16 + 0.3);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + i * 0.16);
      osc.stop(now + i * 0.16 + 0.32);
    });
    setTimeout(() => void ctx.close(), 1000);
  } catch {
    audio ??= new Audio();
  }
}

/** Native notification: Tauri plugin on desktop, Web Notifications in browsers. Only when the window is not focused. */
export async function notify(title: string, body: string): Promise<void> {
  if (document.hasFocus()) return;
  if (isTauri) {
    const n = await import('@tauri-apps/plugin-notification');
    let granted = await n.isPermissionGranted();
    if (!granted) granted = (await n.requestPermission()) === 'granted';
    if (granted) n.sendNotification({ title, body });
    return;
  }
  if (!('Notification' in window)) return;
  if (Notification.permission === 'default') await Notification.requestPermission();
  if (Notification.permission === 'granted') new Notification(title, { body, icon: './favicon.svg' });
}

export function applyTheme(theme: 'light' | 'dark' | 'system'): void {
  const dark = theme === 'dark' || (theme === 'system' && matchMedia('(prefers-color-scheme: dark)').matches);
  document.documentElement.classList.toggle('dark', dark);
}

export function storedTheme(): 'light' | 'dark' | 'system' {
  try {
    return (localStorage.getItem('theme') as 'light' | 'dark' | 'system' | null) ?? 'system';
  } catch {
    return 'system';
  }
}
